"use client";

import { ChevronLeft, ChevronRight, Home } from "lucide-react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
  isActive?: boolean;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  showBackButton?: boolean;
  showHomeButton?: boolean;
  onBackClick?: () => void;
  className?: string;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  showBackButton = true,
  showHomeButton = false,
  onBackClick,
  className = "",
}) => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else if (items.length > 1) {
      const previousItem = items[items.length - 2];
      if (previousItem.href) {
        navigate(previousItem.href);
      } else if (previousItem.onClick) {
        previousItem.onClick();
      }
    } else {
      navigate(-1);
    }
  };

  const handleItemClick = (item: BreadcrumbItem) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.href) {
      navigate(item.href);
    }
  };

  return (
    <nav className={`flex items-center space-x-2 text-sm ${className}`} aria-label="Breadcrumb">
      <div className="flex items-center space-x-2">
        {showBackButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackClick}
            className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Go back"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}

        {showHomeButton && (
          <Link to="/">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-800"
              title="Home"
            >
              <Home className="h-4 w-4" />
            </Button>
          </Link>
        )}

        <ol className="flex items-center space-x-1">
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isClickable = !isLast && (item.href || item.onClick);

            return (
              <li key={index} className="flex items-center">
                {index > 0 && (
                  <ChevronRight className="h-3 w-3 text-muted-foreground mx-1" />
                )}
                
                {isClickable ? (
                  <button
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className="px-3 py-1.5 rounded-md bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors duration-200"
                  >
                    {item.label}
                  </button>
                ) : (
                  <span
                    className={`px-3 py-1.5 rounded-md transition-colors duration-200 ${
                      isLast
                        ? "text-foreground font-medium bg-primary/10"
                        : "text-muted-foreground bg-muted/30"
                    }`}
                  >
                    {item.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};